import { useEffect } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { Box, Column, InputSelect, Row, Text } from '~/commonComponents';
import { JobPostingStatus } from '~/domain/PermanentJobs';
import { UserSubProfession } from '~/enums/UserProfession';
import { useDebounce, useProfessionOptions } from '~/hooks';
import { useIsParentOfficeSelector } from '~/hooks/useIsParentOfficeSelector';
import { FilterHorizontalIcon } from '~/images/icons';
import { Colors } from '~/themes/colors';
import { InputSearchOffice, JobPostingStatusFilterPanel } from '../../components';
import { CreatePostingButton } from '../../CreatePostingButton';

export interface IFilterFormData {
  officeId?: string;
  profession?: string;
  subProfession?: string;
  status?: JobPostingStatus;
}

interface IHeaderProps {
  statistics: Record<JobPostingStatus, number>;
  onValueChanges?: (values: IFilterFormData) => void;
}

export const Header = ({ statistics, onValueChanges }: IHeaderProps) => {
  const { control, setValue, resetField, watch } = useForm<IFilterFormData>({
    defaultValues: {
      officeId: '',
      profession: 'all',
      status: JobPostingStatus.Active,
      subProfession: undefined,
    },
  });

  const watchSubProfession = useWatch({ control, name: 'subProfession' });

  const isParentOffice = useIsParentOfficeSelector();
  const professions = useProfessionOptions({ isShowAllOpt: true }).map((it) => ({
    title: it.label,
    value: it.value,
  }));

  const handleValueChanges = useDebounce((formValues: IFilterFormData) =>
    onValueChanges?.(formValues),
  );

  useEffect(() => {
    const subscription = watch((values) => {
      handleValueChanges(values);
    });
    return () => subscription.unsubscribe();
  }, [handleValueChanges, watch]);

  return (
    <Column marginTop={24} paddingInline={20}>
      <Row alignItems="center">
        <Row gap={4} marginRight={12}>
          <FilterHorizontalIcon />
          <Text bold fontSize={18}>
            Filter by:
          </Text>
        </Row>
        {isParentOffice && (
          <Row alignItems="center" marginRight={20}>
            <Box width={432} marginInline={8}>
              <Controller
                control={control}
                name="officeId"
                render={({ field }) => <InputSearchOffice {...field} />}
              />
            </Box>
            <Text
              bold
              color={Colors.secondary_500}
              cursor="pointer"
              fontSize={18}
              role="button"
              onClick={() => resetField('officeId')}
            >
              Clear
            </Text>
          </Row>
        )}
        <Box width={256} marginRight={20}>
          <Controller
            control={control}
            name="profession"
            render={({ field }) => (
              <InputSelect
                isSelectVisible={false}
                options={professions}
                value={watchSubProfession || field.value || 'all'}
                setValue={(newValue: string) => {
                  const isEFDASelected = newValue === UserSubProfession.EFDA;

                  if (!isEFDASelected) {
                    field.onChange(newValue);
                    setValue('subProfession', undefined);
                    return;
                  }

                  field.onChange(UserSubProfession.getProfession(newValue));
                  setValue('subProfession', newValue);
                }}
              />
            )}
          />
        </Box>
      </Row>

      <Row marginTop={32} paddingInline={48} justifyContent="space-between">
        <Controller
          control={control}
          name="status"
          render={({ field }) => (
            <JobPostingStatusFilterPanel counts={statistics} onChange={field.onChange} />
          )}
        />
        <CreatePostingButton text="Post New Job" variant="lite" />
      </Row>
    </Column>
  );
};
